import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, MoreHorizontal, Plus, Trash2 } from 'lucide-react';
import { type WorkflowCategory, type WorkflowStatusView } from '@apm/contracts';
import { Button } from '../../../components/ui/button';
import { ColorPicker } from './color-picker';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';

/**
 * Thêm / sửa / đổi thứ tự / xoá **cột board** — spec mục 1.
 *
 * ## Vì sao cột tuỳ biến, nhưng nhóm thì không
 *
 * Mỗi team một quy trình: có team cần "Đang review", team khác cần "Chờ QA"
 * hay "Đã deploy". Nên **cột** thì thêm bao nhiêu cũng được — nhưng mỗi cột
 * phải thuộc một trong ba **nhóm** (`chưa làm` / `đang làm` / `xong`), vì
 * burndown, cycle time và CFD cần biết đâu là ba mốc đó. Nhóm là phép đo, cột
 * là quy trình; đây là hai thứ khác nhau và giao diện phải nói rõ điều đó.
 *
 * API đã có từ `APM-501`; phần thiếu là chỗ bấm. Không có nó thì "board tuỳ
 * biến" chỉ tồn tại trong tài liệu.
 */
const CATEGORIES: readonly WorkflowCategory[] = ['todo', 'in_progress', 'done'];

interface ColumnMenuProps {
  readonly status: WorkflowStatusView;
  readonly canMoveLeft: boolean;
  readonly canMoveRight: boolean;
  readonly taskCount: number;
  readonly onRename: (name: string) => void;
  readonly onCategory: (category: WorkflowCategory) => void;
  readonly onWipLimit: (wipLimit: number | null) => void;
  /**
   * Đổi màu cột — APM-BOARD-j. `null` = bỏ màu, quay về màu suy từ nhóm.
   *
   * Màu đặt được lúc tạo mà không đổi được sau đó là một cánh cửa một chiều:
   * người dùng chỉ còn cách xoá cột rồi tạo lại, và làm thế là mất hết việc
   * trong cột ấy.
   */
  readonly onColor: (color: string | null) => void;
  readonly onMove: (direction: -1 | 1) => void;
  readonly onDelete: () => void;
  /**
   * Đổi chủ sở hữu cột — vào khung sprint hay ra ngoài khung (APM-BOARD-b).
   *
   * Bỏ trống khi thao tác ấy không có nghĩa: không có sprint nào đang được xem
   * thì "đưa vào sprint" không trỏ tới sprint nào cả.
   */
  readonly onZoneChange?: (() => void) | undefined;
  /** Cột đang nằm trong khung sprint hay ngoài khung. */
  readonly inSprint?: boolean;
}

/** Menu của một cột — nằm ngay trên tiêu đề cột đó. */
export function ColumnMenu({
  status,
  canMoveLeft,
  canMoveRight,
  taskCount,
  onRename,
  onCategory,
  onWipLimit,
  onColor,
  onMove,
  onDelete,
  onZoneChange,
  inSprint = false,
}: ColumnMenuProps): ReactNode {
  const { t } = useTranslation();
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState(status.name);

  if (renaming) {
    return (
      <form
        className="flex items-center gap-1"
        onSubmit={(event) => {
          event.preventDefault();
          const trimmed = name.trim();
          if (trimmed.length > 0 && trimmed !== status.name) onRename(trimmed);
          setRenaming(false);
        }}
      >
        <input
          autoFocus
          value={name}
          onChange={(event) => setName(event.target.value)}
          aria-label={t('board.columnName')}
          className="h-7 w-32 rounded-control border border-border-strong bg-background px-1.5 text-sm"
        />
        <Button type="submit" size="sm" className="h-7 px-2">
          {t('common.save')}
        </Button>
      </form>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={t('board.columnMenu', { name: status.name })}
        className="rounded p-0.5 text-muted-foreground hover:bg-background"
      >
        <MoreHorizontal className="size-4" />
      </DropdownMenuTrigger>

      <DropdownMenuContent align="start">
        <DropdownMenuItem onSelect={() => setRenaming(true)}>
          {t('board.renameColumn')}
        </DropdownMenuItem>

        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-xs text-muted-foreground">
          {t('board.columnColor')}
        </DropdownMenuLabel>
        {/* Bảng màu nằm thẳng trong menu, không phải một hộp thoại nữa: đổi màu
            là một cú bấm, và một hộp thoại cho một cú bấm là hai cú bấm thừa.
            `onSelect` chặn đóng menu để chọn xong còn thấy kết quả. */}
        <div
          className="px-2 py-1"
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
        >
          <ColorPicker value={status.color} onChange={onColor} />
        </div>

        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-xs text-muted-foreground">
          {t('board.columnCategory')}
        </DropdownMenuLabel>
        {CATEGORIES.map((category) => (
          <DropdownMenuItem
            key={category}
            onSelect={() => onCategory(category)}
            className={category === status.category ? 'font-medium' : undefined}
          >
            {t(`board.category.${category}`)}
            {category === status.category ? <span className="ml-auto">✓</span> : null}
          </DropdownMenuItem>
        ))}

        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => onWipLimit(promptWipLimit(status.wipLimit))}>
          {status.wipLimit === null
            ? t('board.setWipLimit')
            : t('board.changeWipLimit', { limit: status.wipLimit })}
        </DropdownMenuItem>

        <DropdownMenuSeparator />
        <DropdownMenuItem disabled={!canMoveLeft} onSelect={() => onMove(-1)}>
          <ChevronLeft aria-hidden className="size-4" />
          {t('board.moveColumnLeft')}
        </DropdownMenuItem>
        <DropdownMenuItem disabled={!canMoveRight} onSelect={() => onMove(1)}>
          <ChevronRight aria-hidden className="size-4" />
          {t('board.moveColumnRight')}
        </DropdownMenuItem>

        {onZoneChange ? (
          <>
            <DropdownMenuSeparator />
            {/*
              Đường bàn phím cho thao tác kéo cột qua khung. Kéo thả là cách
              nhanh nhất nhưng nó **chỉ có** với chuột — và đây là thao tác đổi
              chủ sở hữu cột, không phải một tiện ích trang trí.
            */}
            <DropdownMenuItem onSelect={onZoneChange}>
              {inSprint ? t('board.moveColumnOutside') : t('board.moveColumnIntoSprint')}
            </DropdownMenuItem>
          </>
        ) : null}

        <DropdownMenuSeparator />
        {/*
          Cột còn thẻ thì **không** cho xoá ở đây: server từ chối (task còn trỏ
          vào `status_id`), nên một mục bấm được rồi báo lỗi chỉ dạy người dùng
          rằng menu này không đáng tin. Nói luôn số thẻ còn lại.
        */}
        <DropdownMenuItem
          disabled={taskCount > 0 || status.isDefault}
          onSelect={onDelete}
          className="text-destructive"
        >
          <Trash2 aria-hidden className="size-4" />
          {taskCount > 0
            ? t('board.deleteColumnBlocked', { count: taskCount })
            : status.isDefault
              ? t('board.deleteColumnDefault')
              : t('board.deleteColumn')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * WIP limit hỏi bằng `prompt`.
 *
 * ⚠️ Có chủ đích, và là ngoại lệ duy nhất: một ô nhập số nằm trong menu cần
 * form riêng, validate riêng, trạng thái riêng — cho một con số mà một dự án
 * đặt vài lần trong cả vòng đời. Đổi thành form khi có người dùng nói họ đặt
 * WIP limit thường xuyên.
 */
function promptWipLimit(current: number | null): number | null {
  const answer = window.prompt('WIP limit (bỏ trống = không giới hạn)', current?.toString() ?? '');
  if (answer === null) return current;

  const trimmed = answer.trim();
  if (trimmed.length === 0) return null;

  const parsed = Number(trimmed);
  return Number.isInteger(parsed) && parsed > 0 && parsed <= 999 ? parsed : current;
}

interface AddColumnProps {
  readonly pending: boolean;
  /**
   * `inSprint` = cột chỉ thuộc sprint đang xem; ngược lại là cột hiện ở **mọi**
   * sprint — APM-BOARD-b.
   */
  readonly onCreate: (
    input: { name: string; category: WorkflowCategory; color?: string },
    inSprint: boolean,
  ) => void;
  /**
   * Tên sprint đang xem. Bỏ trống ⇒ không có sprint nào để đặt cột vào, nên
   * phần chọn phạm vi biến mất thay vì hiện một lựa chọn không dùng được.
   */
  readonly sprintName?: string | undefined;
}

/** Cột "＋" ở cuối hàng — chỗ thêm một chặng mới cho quy trình của team. */
export function AddColumn({ pending, onCreate, sprintName }: AddColumnProps): ReactNode {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [category, setCategory] = useState<WorkflowCategory>('in_progress');
  /** `null` = để `statusTone` tự suy từ nhóm — xem `ColorPicker`. */
  const [color, setColor] = useState<string | null>(null);
  /**
   * Mặc định là **trong sprint** khi đang xem một sprint.
   *
   * Người ta thêm cột lúc đang nhìn một board cụ thể, và cột thêm giữa chừng
   * thường là một chặng của riêng đợt ấy ("Chờ QA lần này"). Mặc định "mọi
   * sprint" thì mỗi lần thử một quy trình mới sẽ để lại một cột vĩnh viễn.
   */
  const [inSprint, setInSprint] = useState(true);

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex h-fit w-40 shrink-0 items-center justify-center gap-1.5 rounded-DEFAULT border border-dashed border-border p-3 text-sm text-muted-foreground transition-colors hover:bg-muted/50"
      >
        <Plus aria-hidden className="size-4" />
        {t('board.addColumn')}
      </button>
    );
  }

  return (
    <form
      className="flex w-56 shrink-0 flex-col gap-2 rounded-DEFAULT border border-border p-2"
      onSubmit={(event) => {
        event.preventDefault();
        const trimmed = name.trim();
        if (trimmed.length === 0) return;
        onCreate(
          { name: trimmed, category, ...(color === null ? {} : { color }) },
          inSprint && sprintName !== undefined,
        );
        setName('');
        setColor(null);
        setOpen(false);
      }}
    >
      <input
        autoFocus
        value={name}
        onChange={(event) => setName(event.target.value)}
        placeholder={t('board.columnNamePlaceholder')}
        aria-label={t('board.columnName')}
        className="h-8 rounded-control border border-border-strong bg-background px-2 text-sm"
      />

      <label className="flex flex-col gap-1 text-xs text-muted-foreground">
        {t('board.columnCategory')}
        <select
          value={category}
          onChange={(event) => setCategory(event.target.value as WorkflowCategory)}
          className="h-8 rounded-control border border-border-strong bg-background px-1 text-sm text-foreground"
        >
          {CATEGORIES.map((value) => (
            <option key={value} value={value}>
              {t(`board.category.${value}`)}
            </option>
          ))}
        </select>
      </label>

      {/* Nói ra vì sao phải chọn nhóm: đây là chỗ duy nhất người dùng gặp khái
          niệm ấy, và chọn sai thì burndown đếm sai mà không có gì báo. */}
      <p className="text-[11px] text-muted-foreground">{t('board.categoryHint')}</p>

      {/*
        Màu cột — APM-BOARD-j.

        Đặt **sau** nhóm có chủ đích: nhóm quyết định màu mặc định, nên chọn
        nhóm trước rồi mới thấy có cần đổi màu hay không. Hỏi màu trước thì
        người dùng chọn một màu, rồi đổi nhóm, và tự hỏi vì sao màu không đổi
        theo.
      */}
      <label className="flex flex-col gap-1 text-xs text-muted-foreground">
        {t('board.columnColor')}
        <ColorPicker value={color} onChange={setColor} />
      </label>

      {/*
        Phạm vi hỏi thành câu, không suy từ chỗ bấm: cột "mọi sprint" và cột
        "riêng sprint này" là hai thứ khác hẳn nhau về vòng đời, và chọn nhầm
        chỉ lộ ra ở sprint sau.
      */}
      {sprintName === undefined ? null : (
        <fieldset className="flex flex-col gap-1">
          <legend className="text-xs text-muted-foreground">{t('board.columnScope')}</legend>
          <label className="flex items-center gap-1.5 text-xs">
            <input
              type="radio"
              name="column-scope"
              checked={inSprint}
              onChange={() => setInSprint(true)}
            />
            {t('board.scopeThisSprint', { name: sprintName })}
          </label>
          <label className="flex items-center gap-1.5 text-xs">
            <input
              type="radio"
              name="column-scope"
              checked={!inSprint}
              onChange={() => setInSprint(false)}
            />
            {t('board.scopeEverySprint')}
          </label>
        </fieldset>
      )}

      <div className="flex gap-1">
        <Button type="submit" size="sm" disabled={pending || name.trim().length === 0}>
          {t('board.addColumn')}
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={() => setOpen(false)}>
          {t('common.cancel')}
        </Button>
      </div>
    </form>
  );
}
